import { useState } from "react";
import { CLP } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AttendanceToggle } from "@/components/AttendanceToggle";
import { formatSessionRange } from "@/lib/format";
import { StateIcon } from "@/components/SessionStateIcons";
import { MapPin, CalendarDays, Clock, User, CalendarCheck, ShieldCheck, ListOrdered, Navigation, Gift, Loader2, CalendarPlus, AlertTriangle, ZoomIn } from "lucide-react";
import { isUrgentTicket } from "@/lib/urgentTickets";
import { giftWhatsAppUrl, thanksWhatsAppUrl } from "@/lib/giftMessage";
import { api } from "@/lib/api";

const TZ = "America/Santiago";
export const fDate = (iso) => iso ? new Date(iso).toLocaleDateString("es-CL", { timeZone: TZ, day: "2-digit", month: "2-digit", year: "numeric" }) : "—";
export const fTime = (iso) => new Date(iso).toLocaleTimeString("es-CL", { timeZone: TZ, hour: "2-digit", minute: "2-digit", hour12: false });
const fLong = (iso) => { const s = new Date(iso).toLocaleDateString("es-CL", { timeZone: TZ, weekday: "long", day: "numeric", month: "long" }); return s.charAt(0).toUpperCase() + s.slice(1); };

const fSlash = (iso) => fDate(iso).replace(/-/g, "/");
const qrSrc = (s) => `${process.env.REACT_APP_BACKEND_URL}${s.qr_url}`;

export function QrZoomDialog({ r, s, n, onClose }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-sm text-center" onOpenAutoFocus={(e) => e.preventDefault()} data-testid="qr-zoom-dialog">
        <DialogHeader className="text-center sm:text-center">
          <DialogTitle className="break-words" data-testid="qr-zoom-workshop">{r.workshop_name}</DialogTitle>
          <DialogDescription data-testid="qr-zoom-session">Sesión {n} | {fSlash(s.starts_at)} - {fTime(s.starts_at)}</DialogDescription>
        </DialogHeader>
        <img src={qrSrc(s)} alt={`QR sesión ${n}`} className="w-full max-w-[280px] aspect-square mx-auto rounded-xl bg-white p-2 border" data-testid="qr-zoom-img" />
      </DialogContent>
    </Dialog>
  );
}

function SessionQr({ r, s, n, size, children, testId }) {
  const [zoom, setZoom] = useState(false);
  return (
    <div className="relative flex items-center gap-3 rounded-2xl bg-secondary p-3 pr-10">
      <img src={qrSrc(s)} alt={`QR sesión ${n}`} className={`${size} rounded-lg bg-white p-1 border shrink-0`} data-testid={testId} />
      <div className="text-xs text-muted-foreground min-w-0">{children}</div>
      <button type="button" onClick={() => setZoom(true)} aria-label="Agrandar QR" className="absolute top-2 right-2 grid place-items-center w-8 h-8 rounded-full bg-background border text-primary hover:bg-primary hover:text-primary-foreground transition-colors" data-testid={`${testId}-zoom`}>
        <ZoomIn className="w-4 h-4" />
      </button>
      {zoom && <QrZoomDialog r={r} s={s} n={n} onClose={() => setZoom(false)} />}
    </div>
  );
}

const Field = ({ icon: Icon, label, children, testId }) => (
  <div className="flex items-start gap-2.5">
    <Icon className="w-4 h-4 mt-0.5 text-primary shrink-0" />
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <div className="text-sm font-medium break-words" data-testid={testId}>{children}</div>
    </div>
  </div>
);

function SessionsListDialog({ r, onClose, onChanged }) {
  const now = new Date().toISOString();
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-sm max-h-[80vh] overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()} data-testid="ticket-sessions-dialog">
        <DialogHeader>
          <DialogTitle>Sesiones del taller</DialogTitle>
          <DialogDescription className="break-words">{r.workshop_name}</DialogDescription>
        </DialogHeader>
        <ol className="space-y-2">
          {r.sessions.map((s, i) => {
            const past = s.starts_at < now;
            const next = s.starts_at === r.next_session_at;
            return (
              <li key={s.id} className={`rounded-lg border p-2.5 text-sm space-y-2 ${next ? "border-primary bg-primary/5" : ""} ${past ? "opacity-70" : ""}`} data-testid={`ticket-session-${s.id}`}>
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0">{i + 1}</span>
                  <span className="flex-1" data-testid={`ticket-session-range-${s.id}`}>{formatSessionRange(s.starts_at, r.duration_min || 0)}</span>
                  {next && <Badge className="shrink-0">Próxima</Badge>}
                  {s.state && <StateIcon state={s.state} testId={`ticket-session-state-${s.id}`} />}
                </div>
                {s.checkin_method && <p className="text-xs font-semibold text-emerald-700" data-testid={`ticket-session-checkin-${s.id}`}>Asistencia registrada ({s.checkin_method.toUpperCase()})</p>}
                {s.qr_url && !s.checkin_method && (!past || s.scan_open) && (
                  <SessionQr r={r} s={s} n={i + 1} size="w-20 h-20" testId={`ticket-session-qr-${s.id}`}>
                    <p className="font-semibold text-foreground">QR de la sesión {i + 1}</p>
                    <p>Muéstralo al llegar al taller</p>
                  </SessionQr>
                )}
                {!past && <AttendanceToggle r={r} s={s} onChanged={onChanged} compact />}
              </li>
            );
          })}
        </ol>
      </DialogContent>
    </Dialog>
  );
}

function PolicyDialog({ r, onClose, onCancel }) {
  const [busy, setBusy] = useState(false);
  const hours = r.policy_cancel_hours || 24;
  const deadline = new Date(new Date(r.starts_at).getTime() - hours * 3600 * 1000);
  const canCancel = deadline > new Date();
  const go = async () => { setBusy(true); await onCancel(r); setBusy(false); };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-sm" data-testid="ticket-policy-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-primary" />Política de cancelación</DialogTitle>
          <DialogDescription>El Maestro de este taller exige cancelar con <strong>{hours} horas</strong> de anticipación.</DialogDescription>
        </DialogHeader>
        <div className={`rounded-xl border p-3 text-sm ${canCancel ? "border-primary/30 bg-primary/5" : "border-amber-300 bg-amber-50 text-amber-800"}`} data-testid="ticket-policy-deadline">
          {canCancel
            ? <>Puedes cancelar tu reserva hasta el <strong>{fDate(deadline.toISOString())} a las {fTime(deadline.toISOString())} hrs</strong>.</>
            : <>El plazo para cancelar tu reserva venció el {fDate(deadline.toISOString())} a las {fTime(deadline.toISOString())} hrs.</>}
        </div>
        <p className="text-xs text-muted-foreground">Si cancelas fuera de este plazo, el Maestro no estará obligado a devolver el dinero.</p>
        {canCancel && (
          <Button variant="outline" className="text-destructive border-destructive/40 hover:bg-destructive/5" onClick={go} disabled={busy} data-testid={`cancel-btn-${r.id}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Cancelar mi reserva"}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CalendarDialog({ r, onClose }) {
  const b = r.business || {};
  const ics = `${process.env.REACT_APP_BACKEND_URL}/api/reservations/${r.codigo_qr}/calendar.ics`;
  const g = (iso) => new Date(iso).toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const next = r.next_session_at;
  const end = next && new Date(new Date(next).getTime() + (r.duration_min || 120) * 60000).toISOString();
  const gUrl = next && `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(r.workshop_name)}&dates=${g(next)}/${g(end)}&location=${encodeURIComponent([b.address, b.commune].filter(Boolean).join(", "))}&details=${encodeURIComponent(`Maestro: ${b.name || ""} · Ticket ${r.codigo_qr}`)}`;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-sm" data-testid="ticket-calendar-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarPlus className="w-5 h-5 text-primary" />Agregar al calendario</DialogTitle>
          <DialogDescription>Guarda las sesiones de este taller con un recordatorio 2 horas antes.</DialogDescription>
        </DialogHeader>
        <Button asChild className="h-auto py-3 justify-start whitespace-normal w-full min-w-0"><a href={ics} data-testid="calendar-ics-btn">
          <CalendarPlus className="w-5 h-5 mr-2 shrink-0" /><span className="text-left"><span className="block">Todas las sesiones ({r.sessions.length})</span><span className="block text-xs opacity-80 font-normal">Calendario del celular: iPhone, Android, Outlook</span></span>
        </a></Button>
        {gUrl && <Button asChild variant="outline" className="h-auto py-3 justify-start whitespace-normal w-full min-w-0"><a href={gUrl} target="_blank" rel="noreferrer" data-testid="calendar-google-btn">
          <CalendarDays className="w-5 h-5 mr-2 shrink-0 text-primary" /><span className="text-left"><span className="block">Google Calendar</span><span className="block text-xs text-muted-foreground font-normal">Próxima sesión: {fDate(next)} · {fTime(next)} hrs</span></span>
        </a></Button>}
      </DialogContent>
    </Dialog>
  );
}

export function TicketSlide({ r, onCancel, onChanged }) {
  const [view, setView] = useState(null);
  const nextS = r.sessions?.find((s) => s.starts_at === r.next_session_at);
  const qrS = r.sessions?.find((s) => s.scan_open && !s.checkin_method && s.starts_at <= new Date().toISOString()) || nextS;
  const canje = r.kind === "canje";
  const b = r.business || {};
  const place = [b.address, b.commune].filter(Boolean).join(", ");
  const live = r.first_session_at && r.first_session_at <= new Date().toISOString();
  const urgent = isUrgentTicket(r);
  const giftPending = r.es_regalo && r.gift?.status === "pending";
  const canThank = r.es_regalo && r.gift?.status === "accepted" && r.gift.giver_phone && !r.gift.thanked;
  const thank = () => { api.post(`/gifts/${r.gift.token}/thanked`).then(() => onChanged?.()).catch(() => {}); };
  return (
    <div className="rounded-3xl overflow-hidden border bg-card shadow-[0_14px_40px_-16px_rgba(121,68,228,0.45)]" data-testid={`ticket-${r.id}`}>
      {r.maestro_suspended && (
        <div className="bg-destructive text-white px-5 py-2.5 text-xs flex items-start gap-2" data-testid={`ticket-suspended-${r.id}`}>
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span><strong>Sesiones suspendidas.</strong> Este Maestro está en revisión por VIVOTaller. No asistas hasta recibir novedades; te contactaremos.</span>
        </div>
      )}
      <div className="bg-primary text-primary-foreground p-5 relative">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] uppercase tracking-[0.2em] opacity-80">VIVOTaller · Ticket</p>
          <div className="flex gap-1.5 items-center">
            {urgent && (
              <span className="grid place-items-center w-7 h-7 rounded-full bg-amber-400 text-[#3d2b00] animate-pulse" title="Confirma tu asistencia: tu próxima sesión inicia en menos de 24 horas" data-testid={`ticket-urgent-${r.id}`}>
                <AlertTriangle className="w-4 h-4" />
              </span>
            )}
            {canje && <Badge className="bg-white text-primary hover:bg-white" data-testid={`ticket-canje-${r.id}`}>Canje</Badge>}
            <Badge className={live ? "bg-sun text-[#3d2b00] hover:bg-sun" : "bg-white/20 hover:bg-white/20"} data-testid={`ticket-state-${r.id}`}>{live ? "En curso" : "Próximo"}</Badge>
          </div>
        </div>
        <h3 className="font-display font-extrabold text-xl mt-2 leading-tight break-words" data-testid={`ticket-name-${r.id}`}>{r.workshop_name}</h3>
        <p className="text-sm opacity-90 mt-1 flex items-center gap-1.5"><User className="w-3.5 h-3.5" />Maestro: <strong>{b.name || "—"}</strong></p>
        {urgent && (
          <p className="mt-3 flex items-start gap-1.5 rounded-xl bg-amber-400 text-[#3d2b00] px-3 py-2 text-xs font-semibold" data-testid={`ticket-urgent-msg-${r.id}`}>
            <AlertTriangle className="w-4 h-4 shrink-0" />Tu próxima sesión inicia en menos de 24 horas y aún no confirmas tu asistencia.
          </p>
        )}
      </div>
      <div className="relative flex items-center">
        <div className="w-5 h-5 rounded-full bg-background -ml-2.5 border-r" />
        <div className="flex-1 border-t-2 border-dashed border-border" />
        <div className="w-5 h-5 rounded-full bg-background -mr-2.5 border-l" />
      </div>
      <div className="p-5 space-y-3.5">
        <div className="grid grid-cols-2 gap-3">
          <Field icon={CalendarDays} label="Inicia" testId={`ticket-start-${r.id}`}>{fDate(r.first_session_at)}</Field>
          <Field icon={CalendarDays} label="Termina" testId={`ticket-end-${r.id}`}>{fDate(r.last_session_at)}</Field>
        </div>
        <Field icon={Clock} label="Próxima sesión" testId={`ticket-next-${r.id}`}>
          {r.next_session_at ? <span data-testid={`ticket-next-range-${r.id}`}>{formatSessionRange(r.next_session_at, r.duration_min || 0)}</span> : "Sin sesiones próximas"}
        </Field>
        {nextS && (
          <div className="rounded-2xl border border-primary/25 bg-primary/5 p-3 space-y-2" data-testid={`ticket-attendance-${r.id}`}>
            <p className="text-xs font-semibold">¿Asistirás a la próxima sesión?</p>
            <AttendanceToggle r={r} s={nextS} onChanged={onChanged} />
          </div>
        )}
        <Field icon={MapPin} label="Lugar" testId={`ticket-address-${r.id}`}>
          {b.address_label && <span className="block text-primary">{b.address_label}</span>}
          {place || "—"}
          {b.address_reference && <span className="block text-xs text-muted-foreground font-normal">{b.address_reference}</span>}
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field icon={CalendarCheck} label="Reservado el" testId={`ticket-booked-${r.id}`}>{fDate(r.created_at)}</Field>
          <Field icon={ListOrdered} label="Cupos" testId={`ticket-qty-${r.id}`}>{r.quantity} · {r.direct_price_clp ? `Valor del taller ${CLP(r.direct_price_clp)}` : r.amount_clp ? `Valor del taller ${CLP(r.amount_clp)}` : "Gratis"}</Field>
        </div>
        {r.es_regalo && r.gift && !giftPending && <Badge variant="outline" className="gap-1" data-testid={`ticket-gift-badge-${r.id}`}><Gift className="w-3 h-3" />{r.gift.status === "accepted" ? `Regalo de ${r.gift.giver_name}` : `Regalo para ${r.gift.recipient_name}`}</Badge>}
        {canThank && (
          <Button asChild size="sm" className="w-full bg-[#25D366] hover:bg-[#1ebe5a] text-white">
            <a href={thanksWhatsAppUrl({ ...r.gift, workshop_name: r.workshop_name })} target="_blank" rel="noopener noreferrer" onClick={thank} data-testid={`ticket-gift-thanks-${r.id}`}>Agradecer a {r.gift.giver_name} por WhatsApp</a>
          </Button>
        )}
        {giftPending && (
          <div className="rounded-2xl border border-primary/30 bg-primary/5 p-3 space-y-2" data-testid={`ticket-gift-pending-${r.id}`}>
            <p className="text-sm font-semibold flex items-center gap-1.5"><Gift className="w-4 h-4 text-primary" />Regalo pendiente para {r.gift.recipient_name}</p>
            <p className="text-xs text-muted-foreground">Cuando {r.gift.recipient_name} abra el enlace y reciba el regalo, el ticket pasará a su cuenta.</p>
            <Button asChild size="sm" className="w-full bg-[#25D366] hover:bg-[#1ebe5a] text-white">
              <a href={giftWhatsAppUrl(r)} target="_blank" rel="noopener noreferrer" data-testid={`ticket-gift-whatsapp-${r.id}`}>Enviar regalo por WhatsApp</a>
            </Button>
          </div>
        )}
        {qrS?.qr_url && !giftPending && (
          <SessionQr r={r} s={qrS} n={r.sessions.indexOf(qrS) + 1} size="w-24 h-24" testId={`ticket-qr-${r.id}`}>
            <div data-testid={`ticket-qr-label-${r.id}`}>
              <p className="font-semibold text-foreground">QR de la sesión {r.sessions.indexOf(qrS) + 1} | {fSlash(qrS.starts_at)}</p>
              <p>Muéstralo al llegar al taller</p>
              <p className="mt-1">Cada sesión tiene su propio QR en «Ver sesiones»</p>
            </div>
          </SessionQr>
        )}
        {r.qr_url && !qrS?.qr_url && !giftPending && (
          <div className="flex items-center gap-3 rounded-2xl bg-secondary p-3">
            <img src={`${process.env.REACT_APP_BACKEND_URL}${r.qr_url}`} alt="QR del ticket" className="w-24 h-24 rounded-lg bg-white p-1 border" data-testid={`ticket-qr-${r.id}`} />
            <p className="text-xs text-muted-foreground" data-testid={`ticket-qr-label-${r.id}`}>
              Muestra este código al llegar.
              <span className="block font-mono text-[10px] mt-1 break-all">{r.codigo_qr}</span>
            </p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" size="sm" onClick={() => setView("sessions")} data-testid={`ticket-sessions-btn-${r.id}`}><Clock className="w-4 h-4 mr-1" />Ver sesiones</Button>
          {!canje && <Button variant="outline" size="sm" onClick={() => setView("policy")} data-testid={`ticket-policy-btn-${r.id}`}><ShieldCheck className="w-4 h-4 mr-1" />Cancelación</Button>}
        </div>
        {r.codigo_qr && r.sessions?.length > 0 && (
          <Button variant="secondary" size="sm" className="w-full" onClick={() => setView("calendar")} data-testid={`ticket-calendar-btn-${r.id}`}>
            <CalendarPlus className="w-4 h-4 mr-1.5 text-primary" />Agregar al calendario
          </Button>
        )}
        {(b.maps_url || b.waze_url) && (
          <div className="grid grid-cols-2 gap-2" data-testid={`ticket-directions-${r.id}`}>
            {b.maps_url && <Button asChild size="sm"><a href={b.maps_url} target="_blank" rel="noreferrer"><Navigation className="w-4 h-4 mr-1" />Google Maps</a></Button>}
            {b.waze_url && <Button asChild size="sm" variant="secondary"><a href={b.waze_url} target="_blank" rel="noreferrer"><Navigation className="w-4 h-4 mr-1" />Waze</a></Button>}
          </div>
        )}
      </div>
      {view === "sessions" && <SessionsListDialog r={r} onClose={() => setView(null)} onChanged={onChanged} />}
      {view === "policy" && <PolicyDialog r={r} onClose={() => setView(null)} onCancel={async (x) => { await onCancel(x); setView(null); }} />}
      {view === "calendar" && <CalendarDialog r={r} onClose={() => setView(null)} />}
    </div>
  );
}
